/**
 * File and pay a return (piece 25; decision 1782): the four steps one return
 * period walks from Draft to Paid, each read from the period's own
 * `SalesTaxFiling` row and what the state's Review tab reads. 🔒 Nothing here
 * is stored; the tax controller answers the facts and this function reads them.
 */
import type { SalesTaxFilingStatusValue } from './filings';

export const RETURN_STEPS = ['review', 'figures', 'file', 'pay'] as const;
export type ReturnStep = (typeof RETURN_STEPS)[number];

/** The facts read for one return; a browser fetch path, relative to the API root. */
export const returnStepsPath = (filingId: string): string => `/tax/filings/${encodeURIComponent(filingId)}/steps`;

/** Recompute, the existing write the figures step reaches for a Draft whose stored figures are stale (decision 1782). */
export const returnRecomputePath = (filingId: string): string => `/tax/filings/${encodeURIComponent(filingId)}/recompute`;

/** 🔒 The facts behind the four steps, read from the return's row and none of them stored (decision 1782). */
export interface ReturnStepsView {
  filingId: string;
  stateCode: string;
  stateName: string;
  periodStart: string;
  periodEnd: string;
  dueOn: string;
  status: SalesTaxFilingStatusValue;
  grossSalesCents: number;
  taxableSalesCents: number;
  /** The tax the return states, its adjustments included. */
  taxDueCents: number;
  adjustmentsCents: number;
  filedOn: string | null;
  confirmationRef: string | null;
  paymentId: string | null;
  /** The remittance the return names, as the payment row reads it. */
  payment: { paidOn: string; amountCents: number; reference: string | null } | null;
  /** The state's lines on the review report, which its Review tab draws. */
  reviewLines: number;
  /** The period's gross sales read afresh from the issued invoices, on a Draft alone; null once the return has left Draft. */
  periodSalesCents: number | null;
}

export interface ReturnStepFacts {
  done: boolean;
  /** Whether what the step needs is done; a step that cannot be reached draws as later. */
  reachable: boolean;
}

/**
 * 🔒 Each step's done test (decision 1782). Review is done once the state's
 * review report is empty or the return has left Draft; the figures once the
 * return has left Draft, or it states figures, or the period had no sales;
 * filing once `filedOn` and `confirmationRef` are set; paying once
 * `paymentId` is. A key the answer lacks reads as not done.
 */
export function returnSteps(view: ReturnStepsView): Record<ReturnStep, ReturnStepFacts> {
  const drafted = view.status === 'Draft';
  const stated = (view.grossSalesCents ?? 0) !== 0 || (view.taxDueCents ?? 0) !== 0;
  const noSales = view.periodSalesCents === 0;
  const figures = !drafted || stated || noSales;
  const filed = Boolean(view.filedOn) && Boolean(view.confirmationRef?.trim());
  return {
    review: { done: !drafted || view.reviewLines === 0, reachable: true },
    figures: { done: figures, reachable: true },
    file: { done: filed, reachable: figures },
    pay: { done: Boolean(view.paymentId), reachable: filed },
  };
}
